import { StyleFilterPublicIcon } from '@/components/Icons/StyleFilterPublicIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/utils';
import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { useStyleFilterStore } from '@/state/style-filter-list-store';
import { formatNumberWithCommas } from '@/utils/number';
import { formatUSDTwoDecimal } from '@/utils/number';

interface StyleFilterCardProps {
  title: string;
  dateCreated?: string;
  datePublished?: string;
  isPublic?: boolean;
  id: string | number;
  album_cover?: string;
}

const StyleFilterCard: React.FC<StyleFilterCardProps> = ({ id, title, dateCreated, datePublished, isPublic = false, album_cover }) => {
  const { analyticsData } = useStyleFilterStore();

  const navigate = useNavigate();

  if (isMobile) {
    return (
      <Card className="bg-black text-white py-6 rounded-xl border border-[#242425]">
        <div className="flex flex-col gap-6 px-6">
          <div className="relative w-24 h-32 flex-shrink-0">
            <img
              src={album_cover}
              style={{
                outline: '1px solid rgba(255, 255, 255, 0.15)',
                outlineOffset: '-1px',
              }}
              className="bg-jen-green w-full h-full rounded-lg object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="text-[22px] tracking-[-5%] leading-tight font-normal mb-6 cursor-pointer hover:underline"
              onClick={() => {
                navigate(`/style-filter-admin/${id}?isPublic=${isPublic}`);
              }}
            >
              {title}
            </h1>
            <div className="flex gap-x-16 gap-y-2">
              <div>
                <div className="text-sm">Date Created</div>
                <div className="text-[#B7B7BD]">{dayjs(dateCreated).format('D MMMM YYYY')}</div>
              </div>
              {isPublic && (
                <div>
                  <div className="text-sm">Date Published</div>
                  <div className="text-[#B7B7BD]">{dayjs(datePublished).format('D MMMM YYYY')}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* <div className="inline-block px-6 mt-6">
          <div
            className="px-6 py-2.5 rounded-lg border border-white/20 text-[15px]"
            onClick={() => {
              navigate(`/?filterGroupId=${id}`);
            }}
          >
            Test StyleFilter
          </div>
        </div> */}

        {isPublic && (
          <div className="mt-6">
            <div className="flex items-center justify-center border-t border-b bg-[#4CAF00]/20 text-[#4CAF00]  border-[#1C1C1C] py-4 px-6 font-pressura-mono uppercase gap-2">
              <span>
                <StyleFilterPublicIcon />
              </span>
              <span className="text-[12px]">PUBLIC</span>
            </div>
            <div className="flex justify-between items-center py-4 px-6 ">
              <span className="text-[#71717A]">Total Purchases</span>
              <span className="text-2xl tabular-nums">
                {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.total_purchases)}
              </span>
            </div>
            <div className="flex justify-between items-center py-4 px-6 ">
              <span className="text-[#71717A]">Tracks Generated</span>
              <span className="text-2xl tabular-nums">
                {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.tracks_generated)}
              </span>
            </div>
            <div className="flex justify-between items-center py-4 px-6 ">
              <span className="text-[#71717A]">Seconds Generated</span>
              <span className="text-2xl tabular-nums">
                {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.seconds_generated)}
              </span>
            </div>
            <div className="flex justify-between items-center py-4 px-6 ">
              <span className="text-[#71717A]">Income Generated</span>
              <span className="text-2xl tabular-nums">
                {formatUSDTwoDecimal(analyticsData?.data?.sf_list_detail?.[id]?.revenue_generated)}
                {/* ${formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.revenue_generated)} */}
              </span>
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="bg-black rounded-lg border border-[#242425] overflow-hidden">
      <CardHeader className="p-6 border-b border-[#242425]">
        <div className="flex items-center gap-6">
          <img
            className="w-[86.15px] h-[140px] object-cover rounded-md overflow-hidden"
            src={album_cover}
            style={{
              outline: '1px solid rgba(255, 255, 255, 0.15)',
              outlineOffset: '-1px',
            }}
            alt="Style filters"
          />

          <div className="flex flex-col items-start gap-3 flex-1">
            <h2
              className="text-white self-stretch font-normal text-contextual-colors-content-primary text-[32px] tracking-[-1.60px] leading-8 cursor-pointer hover:underline"
              onClick={() => {
                navigate(`/style-filter-admin/${id}?isPublic=${isPublic}`);
              }}
            >
              {title}
            </h2>

            <div className="flex items-center gap-4 self-stretch w-full">
              <div className="flex flex-col w-[150px] items-start gap-0.5">
                <div className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-4">Date Created</div>
                <div className="self-stretch font-normal text-[#B7B7BD] text-base tracking-[-0.64px] leading-4">
                  {dayjs(dateCreated).format('D MMMM YYYY')}
                </div>
              </div>

              {isPublic && (
                <div className="flex flex-col w-[150px] items-start gap-0.5">
                  <div className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-4">Date Published</div>
                  <div className="self-stretch font-normal text-[#B7B7BD] text-base tracking-[-0.64px] leading-4">
                    {dayjs(datePublished).format('D MMMM YYYY')}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col h-[142px] items-end justify-between">
            <X className="w-4 h-4" />
            {/* <Button
              variant="outline"
              className="bg-black hover:bg-zinc-800 !border !border-[#242425] px-[18px] py-4 rounded-lg"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/?filterGroupId=${id}`);
              }}
            >
              <span className="font-normal text-white hover:text-white text-base tracking-[-0.32px] leading-4">Test StyleFilter</span>
            </Button> */}
          </div>
        </div>
      </CardHeader>

      {isPublic && (
        <CardContent className="p-0">
          <div className="flex flex-row w-full">
            <div className="bg-[#4CAF00]/20 text-[#4CAF00] font-pressura-mono px-4 flex items-center justify-center py-1 text-xs rounded-l mr-2 uppercase border-r border-[#1B3F00] gap-2">
              <span>
                <StyleFilterPublicIcon />
              </span>
              <span>PUBLIC</span>
            </div>
            <div className="flex-1 grid grid-cols-4 gap-4">
              <div className="flex flex-col gap-0.5 border-r border-[#242425] px-6 py-4">
                <span className="text-[16px] self-stretch font-normal text-[#71717A] text-base tracking-[-0.64px] leading-[100%]">
                  Purchases
                </span>
                <span className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-[100%]">
                  {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.total_purchases)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 border-r border-[#242425] px-6 py-4">
                <span className="text-[16px] self-stretch font-normal text-[#71717A] text-base tracking-[-0.64px] leading-[100%]">
                  Tracks Generated
                </span>
                <span className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-[100%]">
                  {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.tracks_generated)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 border-r border-[#242425] px-6 py-4">
                <span className="text-[16px] self-stretch font-normal text-[#71717A] text-base tracking-[-0.64px] leading-[100%]">
                  Seconds Generated
                </span>
                <span className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-[100%]">
                  {formatNumberWithCommas(analyticsData?.data?.sf_list_detail?.[id]?.seconds_generated)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5  px-6 py-4">
                <span className="text-[16px] self-stretch font-normal text-[#71717A] text-base tracking-[-0.64px] leading-[100%]">
                  Income Generated
                </span>
                <span className="self-stretch font-normal text-white text-base tracking-[-0.64px] leading-[100%]">
                  {formatUSDTwoDecimal(analyticsData?.data?.sf_list_detail?.[id]?.revenue_generated)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default StyleFilterCard;
